import React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TailwindProfessionEditor from 'widgets/bussiness/profession-editor-tw';
import { guidIsNullOrEmpty } from 'utils/useful-functions';
import { EventDetailsViewModel, EventFormStore, EventFormViewModel } from 'stores/events/event-form-store';
import { EventFormMode } from 'site/pages/events/event-form/event-form';
import { connect } from 'redux-scaffolding-ts';
import TailwindCategoryFormEditor from 'widgets/bussiness/category-form-editor-tw';
import TailwindPatternMachines from 'site/pages/shared/events-and-requests/pattern-machines-tw';
import { PatternMachinesViewModel } from 'site/pages/shared/events-and-requests/pattern-machines';
import { NonMachineRelatedComponentViewModel } from 'site/pages/shared/events-and-requests/non-machine-related/non-machine-related';
import TailwindNonMachineRelatedComponent from 'site/pages/shared/events-and-requests/non-machine-related/non-machine-related-tw';
import { Categories } from 'stores/requests/requests-store';

interface EventDetailContainerProp {
  event: EventFormViewModel;
  mode: EventFormMode;
  readOnly?: boolean;
  loading: boolean;
  eventFormStore?: EventFormStore;
}

const EventDetailContainer: React.FC<EventDetailContainerProp> = ({ event, mode, readOnly, loading, eventFormStore }) => {
  const { t } = useTranslation();

  const [selectedEvent, setSelecttedEvent] = useState<EventFormViewModel>(event);

  const {
    pattern,
    machines,
    profession,
    category,
    machineRelated,
    nmrCluster,
    nmrFunctionalArea,
    nmrFunctionalSubArea,
    nmrTrainingName
  } = selectedEvent.eventDetails;

  const patternMachines: PatternMachinesViewModel = { pattern, machines };

  const nMR: NonMachineRelatedComponentViewModel = {
    cluster: nmrCluster,
    functionalArea: nmrFunctionalArea,
    functionalSubArea: nmrFunctionalSubArea,
    trainingName: nmrTrainingName
  };

  const handleOnPatternMachinesChange = (newPatternMachines: PatternMachinesViewModel) => {
    if (selectedEvent?.eventDetails) {
      const eventCopy = { ...selectedEvent };
      let eventDetails = { ...eventCopy?.eventDetails };
      eventDetails.pattern = newPatternMachines.pattern;
      eventDetails.machines = newPatternMachines.machines;
      setSelecttedEvent({ ...eventCopy, eventDetails });
      eventFormStore.change({ eventDetails });
    }
  };

  const handleOnEventDetailsChange = (property: keyof EventDetailsViewModel, value: any): void => {
    const eventDetails = { ...selectedEvent?.eventDetails };

    eventDetails[property as string] = value;
    if (property === 'machineRelated' || property === 'category') {
      eventDetails.nmrCluster = null;
      eventDetails.nmrFunctionalArea = null;
      eventDetails.nmrTrainingName = null;
      eventDetails.nmrFunctionalSubArea = null;
    }
    if (property === 'machineRelated' && value) eventDetails.category = 'Technical' as any;
    if (property === 'category' && value === 'Functional') eventDetails.machineRelated = false;

    eventFormStore.change({ eventDetails });
  };

  const handleNMRChange = (value: NonMachineRelatedComponentViewModel) => {
    let eventDetails = { ...selectedEvent?.eventDetails };

    eventDetails.nmrCluster = value.cluster;
    eventDetails.nmrFunctionalArea = value.functionalArea;
    eventDetails.nmrFunctionalSubArea = value.functionalSubArea;
    eventDetails.nmrTrainingName = value.trainingName;

    eventFormStore.change({ eventDetails });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm text-gray-700">{t('Role')}</label>
        <TailwindProfessionEditor
          readOnly={readOnly}
          className="w-full border border-gray-300 p-2 rounded bg-gray-100"
          nullable
          value={guidIsNullOrEmpty(profession?.id) ? null : profession}
          onChange={value => handleOnEventDetailsChange('profession', value)}
        />
      </div>

      <div>
        <label className="text-sm text-gray-700">{t('Category')}</label>
        <TailwindCategoryFormEditor
          readOnly={readOnly}
          className={`w-full border border-gray-300 p-2 rounded  ${readOnly ? 'cursor-not-allowed bg-gray-50 text-gray-500' : ''}`}
          value={category ? Categories[Categories[category]] : null}
          onChange={value => handleOnEventDetailsChange('category', value)}
          clearable
          placeholder={t('Category')}
        />
      </div>

      <div>
        <label className="text-sm text-gray-700 block">{t('Machine Related')}</label>
        <div className="flex gap-4 mt-1">
          <button
            disabled={readOnly}
            className={`px-4 py-1 rounded border ${machineRelated ? 'bg-primary-100 border-blue-400' : 'bg-gray-100 border-gray-300'}`}
            onClick={() => handleOnEventDetailsChange('machineRelated', true)}
          >
            {t('Machine Related')}
          </button>
          <button
            disabled={readOnly}
            className={`px-4 py-1 rounded border ${
              !machineRelated ? 'bg-primary-100 border-blue-400' : readOnly ? 'bg-gray-100 border-gray-300' : 'bg-white-100 border-gray-300'
            }`}
            onClick={() => handleOnEventDetailsChange('machineRelated', false)}
          >
            {t('Non-Machine Related')}
          </button>
        </div>
      </div>

      <div>
        <div className="form__event-details-tab__mr__pattern form__event-details-tab__element mr-table-component__common-styles">
          {machineRelated && (
            <TailwindPatternMachines
              readOnly={readOnly}
              value={patternMachines}
              onChange={handleOnPatternMachinesChange}
              profession={profession}
            />
          )}

          {!machineRelated && (
            <TailwindNonMachineRelatedComponent category={category} value={nMR} readOnly={readOnly} onChange={handleNMRChange} />
          )}
        </div>
      </div>
    </div>
  );
};

export default connect(['eventFormStore', EventFormStore])(EventDetailContainer);
